import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { WallboxHardware } from '@e3dc-react/shell/Libs/EMobility/wallboxes/models/wallbox-hardware.model'
import { ChargingSessionDto } from '../../services/api/e-mobility'
import { transposeArrayType } from 'src/Libs/flow/utils/transpose-array-type'
import { useStorageWallboxes } from '../Dashboard/hooks/dashboard-fetch-hooks'
import { useInstallationDevices, useInstallationSubDevices } from 'src/Libs/flow/installation/hooks'
import { useGetBadges } from 'src/Libs/flow/end-user-settings/badges-for-charging-station/hooks'
import { ChargingSessionModel } from '../charging-session-models'
import { toChargingSessions } from '../charging-session-parser'
import { usePortalQuery } from 'src/Libs/api'

interface UseChargingSessionsReturnType {
  chargingSessions: ChargingSessionModel[]
  wallboxes: WallboxHardware[]
  isLoading: boolean
  isError: boolean
  displayEnergyAll: boolean
}

export const useChargingSessions = (installationId: number, storageId: string): UseChargingSessionsReturnType => {
  const authFetch = useAuthFetchWithResult()

  const { data: rfidCards, isLoading: rfidCardsLoading, isError: isRfidCardsError } = useGetBadges(installationId)
  const nonNullRfidCards = rfidCards?.filter(card => card !== null) ?? []

  const { wallboxes, isWallboxesLoading, isWallboxesError } = useStorageWallboxes(storageId)

  const { data: deviceLinks, isLoading: deviceLinksLoading, isError: isDeviceLinksError } = useInstallationDevices(installationId)

  const emcDeviceId = deviceLinks?.emc?.id?.toString() ?? null

  const {
    data: subDeviceLinks,
    isLoading: subDeviceLinksLoading,
    isError: subDeviceLinksError,
  } = useInstallationSubDevices(installationId, emcDeviceId, true, false)

  const subDevices = (subDeviceLinksError ? [] : subDeviceLinks?.controlled?.filter(device => device.type === 'Evse')) ?? []

  const hasStorage = deviceLinks?.storages && deviceLinks.storages.length > 0
  const hasPvExtern = !!subDeviceLinks?.monitoring?.meters?.some(({ deviceType }) => deviceType === 'PVExtern')

  const displayEnergyAll = hasStorage || hasPvExtern

  /**
   * Charging sessions query
   */
  const {
    data: chargingSessionsRaw,
    isLoading: chargingSessionsLoading,
    isError: isChargingSessionsError,
  } = usePortalQuery({
    queryKey: ['e-mobility', storageId, 'charging-sessions'],
    queryFn: async () => {
      const response = await authFetch<ArrayType<Nullable<ChargingSessionDto>>>({
        path: `e-mobility/${storageId}/charging`,
        endpoint: process.env.REACT_APP_EMOBILITY_ENDPOINT,
        method: 'GET',
      })

      return transposeArrayType(response)
    },
    refetchOnMount: true,
    staleTime: 0,
    enabled: Boolean(storageId),
  })

  const isLoading = chargingSessionsLoading || isWallboxesLoading || deviceLinksLoading || subDeviceLinksLoading || rfidCardsLoading

  const isError = isChargingSessionsError || isDeviceLinksError || isWallboxesError || isRfidCardsError

  const chargingSessions =
    chargingSessionsRaw && subDevices && wallboxes
      ? toChargingSessions(chargingSessionsRaw, subDevices, wallboxes, nonNullRfidCards, displayEnergyAll)
      : []

  return {
    wallboxes: wallboxes ?? [],
    chargingSessions,
    isLoading,
    isError,
    displayEnergyAll,
  }
}
